import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE, getToken, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WidgetError } from "@/components/WidgetError";
import { toast } from "sonner";

const initialWidget = { status: "loading", items: [], error: null };

export default function ReportsPage() {
  const [daily, setDaily] = useState(initialWidget);
  const [byService, setByService] = useState(initialWidget);
  const [byCustomer, setByCustomer] = useState(initialWidget);

  const loadOne = useCallback(async (path, setState) => {
    setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const { data } = await api.get(path);
      setState({ status: "ok", items: data.items || [], error: null });
    } catch (err) {
      setState({ status: "error", items: [], error: err });
    }
  }, []);

  useEffect(() => {
    // allSettled semantics — each widget is independent.
    loadOne("/reports/orders/daily", setDaily);
    loadOne("/reports/orders/by-service", setByService);
    loadOne("/reports/orders/by-customer", setByCustomer);
  }, [loadOne]);

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/reports/export/orders.csv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "orders.csv";
      a.click();
    } catch (e) {
      toast.error(formatError(e, "Could not export CSV"));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label-overline">Management</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">Reports</h1>
        </div>
        <Button data-testid="rpt-export" onClick={exportCsv}
                className="rounded bg-slate-900 text-white hover:bg-slate-800">Export orders CSV</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel
          title="Daily orders"
          state={daily}
          cols={["date", "status", "count"]}
          testId="rpt-daily"
          onRetry={() => loadOne("/reports/orders/daily", setDaily)}
        />
        <Panel
          title="By service"
          state={byService}
          cols={["service", "count"]}
          testId="rpt-by-service"
          onRetry={() => loadOne("/reports/orders/by-service", setByService)}
        />
        <Panel
          title="Top customers"
          state={byCustomer}
          cols={["customer", "count"]}
          testId="rpt-by-customer"
          onRetry={() => loadOne("/reports/orders/by-customer", setByCustomer)}
        />
      </div>
    </div>
  );
}

const Panel = ({ title, state, cols, testId, onRetry }) => (
  <div className="bg-white border border-slate-200" data-testid={testId}>
    <div className="px-4 py-3 border-b border-slate-200 label-overline">{title}</div>
    {state.status === "error" ? (
      <div className="p-4">
        <WidgetError error={state.error} onRetry={onRetry} testId={`${testId}-error`} />
      </div>
    ) : (
      <table className="tbl w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="label-overline text-slate-600">
            {cols.map((c) => <th key={c} className="text-left px-4 py-2">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {state.status === "loading" && (
            <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
          )}
          {state.status === "ok" && state.items.length === 0 && (
            <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-slate-500">No data</td></tr>
          )}
          {state.status === "ok" && state.items.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              {cols.map((c) => (
                <td key={c} className={`px-4 py-2 ${c === "count" ? "font-mono" : ""}`}>{r[c] ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
